import { createContext, useContext, useEffect, useReducer, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'

import type { Dispatch, PropsWithChildren, ReactElement } from 'react'
import type {
  IAccountsParams,
  ICaParams,
  IDauParams,
  IIapListingParams,
  IReviewsMonitoringParams,
  IUaManagementParams,
  IUsageClusteringParams,
  TSearchParamsScope,
} from '~/types/search-params'

import { COOKIES, DEFAULT_APPLICATION } from '~/const'
import { SEARCH_PARAMS_KEYS } from '~/const/search-params'
import { shortcut } from '~/const/shortcut'
import { useShortcut } from '~/hooks/useShortcut'
import { useUserPreferencesQuery } from '~/queries/users'
import { getFiltersNonEmptyField } from '~/utils/filters'
import { isTokenValid } from '~/utils/token'
import { getSearchParamsObject } from '~/utils/url'

interface ISearchParamsContext {
  dispatchToSearchParams: Dispatch<ISearchParamsAction>
  searchParamsObject: ISearchParamsState
}

interface ISearchParamsState
  extends IAccountsParams,
    ICaParams,
    IDauParams,
    IIapListingParams,
    IReviewsMonitoringParams,
    IUaManagementParams,
    IUsageClusteringParams {
  app?: string
  externalId?: string
}

interface ISearchParamsAction {
  type:
    | 'update'
    | 'reset-business-reports-filters'
    | 'reset-ca-filters'
    | 'reset-cluster-competitors-filters'
    | 'reset-iap-filters'
    | 'reset-reviews-monitoring-filters'
  value?: Record<string, string> | Record<string, string[]> | Record<string, string | string[]>
}

const SearchParamsContext = createContext<ISearchParamsContext>({
  dispatchToSearchParams: () => {},
  searchParamsObject: {},
})

const getRelevantParamsForCurrentPage = (pathname: string, state: ISearchParamsState): { [key: string]: string } => {
  const relevantParams: { [key: string]: string } = {}

  for (const key in SEARCH_PARAMS_KEYS) {
    const { KEYS, PATHNAME_PREFIXES } = SEARCH_PARAMS_KEYS[key as TSearchParamsScope]

    if (PATHNAME_PREFIXES.find((el) => pathname.startsWith(el))) {
      for (const key in state) {
        if (KEYS.includes(key) && !!state[key as keyof ISearchParamsState]) {
          relevantParams[key] = state[key as keyof ISearchParamsState] as string
        }
      }
    }
  }

  return relevantParams
}

const removeKeysFromState = (state: ISearchParamsState, keys: string[]) => {
  const filteredKeys = Object.keys(state).filter((key) => !keys.includes(key))
  const filteredState = filteredKeys.reduce((obj, key) => {
    const castedKey = key as keyof ISearchParamsState
    obj[castedKey] = state[castedKey] as (string & string[]) | undefined
    return obj
  }, {} as ISearchParamsState)

  return filteredState
}

const reducer = (state: ISearchParamsState, action: ISearchParamsAction) => {
  switch (action.type) {
    case 'update':
      return { ...state, ...action.value }

    case 'reset-ca-filters':
      return removeKeysFromState(state, SEARCH_PARAMS_KEYS.COHORTED_ANALYTICS.KEYS)

    case 'reset-cluster-competitors-filters':
      return removeKeysFromState(state, SEARCH_PARAMS_KEYS.CLUSTER_COMPETITORS.KEYS)

    case 'reset-business-reports-filters':
      return removeKeysFromState(state, SEARCH_PARAMS_KEYS.BUSINESS_REPORTS.KEYS)

    case 'reset-iap-filters':
      return removeKeysFromState(state, SEARCH_PARAMS_KEYS.IAP_LISTING.KEYS)

    case 'reset-reviews-monitoring-filters':
      return removeKeysFromState(state, SEARCH_PARAMS_KEYS.REVIEWS_MONITORING.KEYS)

    default:
      throw Error(`Unknown action: ${action.type}`)
  }
}

const SearchParamsProvider = ({ children }: PropsWithChildren): ReactElement => {
  /** Local state */

  const [cookies] = useCookies([COOKIES.TOKENS])

  const { hash, pathname } = useLocation()

  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const [currentBasePath, setCurrentBasePath] = useState(pathname.split('/')[1])

  const initialSearchParams: ISearchParamsState = searchParams ? getSearchParamsObject(searchParams.toString()) : {}

  const [searchParamsState, dispatch] = useReducer(reducer, initialSearchParams)

  /** Queries & Mutations */

  const { data: userPreferences } = useUserPreferencesQuery({ enabled: isTokenValid(cookies[COOKIES.TOKENS]) })

  const keyBindingRedirection = (code: string) => {
    const shortcutInfos = userPreferences?.shortcuts?.key_func?.find(({ key_value }) => key_value === code)

    if (shortcutInfos?.app_key && shortcutInfos?.device_type) {
      dispatch({ type: 'update', value: { app: `${shortcutInfos.app_key}:${shortcutInfos.device_type}` } })
    }

    if (shortcutInfos?.view_path) {
      navigate(shortcutInfos?.view_path)
    }
  }

  /** Effects */

  useShortcut({
    ...shortcut.openFavouriteView,
    fn: (code?: string) => {
      code && keyBindingRedirection(code)
    },
  })

  useEffect(() => {
    const basePath = pathname.split('/')[1]
    if (currentBasePath !== basePath) {
      if (!pathname.startsWith('/analytics/ca/')) {
        dispatch({ type: 'reset-ca-filters' })
      }
      if (!pathname.startsWith('/analytics/business-reports/')) {
        dispatch({ type: 'reset-business-reports-filters' })
      }
      if (!pathname.startsWith('/competitors/clusters/')) {
        dispatch({ type: 'reset-cluster-competitors-filters' })
      }
      if (!pathname.startsWith('/in-app-purchases/')) {
        dispatch({ type: 'reset-iap-filters' })
      }
      if (!pathname.startsWith('/versions')) {
        dispatch({ type: 'update', value: { externalId: '' } })
      }
      if (!pathname.startsWith('/reviews/monitoring')) {
        dispatch({ type: 'reset-reviews-monitoring-filters' })
      }

      setCurrentBasePath(basePath)
    }
  }, [currentBasePath, pathname])

  useEffect(() => {
    const scopedParams = getRelevantParamsForCurrentPage(pathname, searchParamsState)

    if (!pathname.startsWith('/ua-dashboard')) {
      if (pathname.startsWith('/versions') && scopedParams['externalId'] && scopedParams['app']) {
        scopedParams['app'] = ''
      }

      const encoded = Object.entries(getFiltersNonEmptyField(scopedParams))
        .map(([k, v]) =>
          Array.isArray(v) ? `${k}=${encodeURIComponent(v.join(','))}` : `${k}=${encodeURIComponent(v)}`
        )
        .join('&')

      navigate(`${pathname}?${encoded}${hash}`, { replace: true })
    }
  }, [hash, navigate, pathname, searchParamsState])

  useEffect(() => {
    const applicationInLocalStorage = localStorage.getItem(COOKIES.STORED_APPLICATION)

    if (!searchParamsState.app) {
      dispatch({ type: 'update', value: { app: applicationInLocalStorage || DEFAULT_APPLICATION } })
    }
  }, [searchParamsState.app])

  /** Render */

  return (
    <SearchParamsContext.Provider value={{ dispatchToSearchParams: dispatch, searchParamsObject: searchParamsState }}>
      {children}
    </SearchParamsContext.Provider>
  )
}

const useSearchParamsContext = (): ISearchParamsContext => {
  return useContext(SearchParamsContext)
}

export { SearchParamsProvider, useSearchParamsContext }
